/**
 * Permissions matrix config for the Create/Edit Role screens.
 *
 * Modules and per-level availability follow the PROLINE-2589 story scope:
 * a 10-module matrix where some cells are "Blocked / Under Review" or
 * "Not Applicable" and must be disabled in the UI. `moduleName` matches the
 * API contract; `key` is used only for localized labels.
 */
import { companyPermissionModules } from "../../../../utils/Constants";
import { applyPermissionDependencies } from "./permissionDependencies";

export const PERMISSION_LEVELS = [
  { key: "view", flag: "can_view" },
  { key: "add", flag: "can_add" },
  { key: "edit", flag: "can_edit" },
  { key: "delete", flag: "can_delete" },
];

export const PERMISSION_GROUPS = [
  {
    key: "coreOperations",
    color: "#5B21B6",
    modules: [
      {
        key: "applicants",
        moduleName: companyPermissionModules.applicants,
        // Add/Edit are Blocked / Under Review per AC #12 — no company add
        // feature exists, and the edit page has no reachable entry point.
        disabledFlags: ["can_add", "can_edit"],
      },
      { key: "employees", moduleName: companyPermissionModules.employees },
      {
        key: "joiningEvents",
        moduleName: companyPermissionModules.joiningEvents,
      },
      { key: "events", moduleName: companyPermissionModules.events },
    ],
  },
  {
    key: "hrFinance",
    color: "#D946EF",
    modules: [
      // Payroll: Delete is Blocked / Under Review in this release.
      {
        key: "payroll",
        moduleName: companyPermissionModules.payroll,
        disabledFlags: ["can_delete"],
      },
      { key: "timesheet", moduleName: companyPermissionModules.timesheet },
    ],
  },
  {
    key: "managements",
    color: "#059669",
    modules: [
      {
        key: "manageClients",
        moduleName: companyPermissionModules.manageClients,
      },
      {
        key: "manageReports",
        moduleName: companyPermissionModules.manageReports,
      },
    ],
  },
  {
    key: "analyticsReports",
    color: "#F59E0B",
    modules: [
      // Evaluations: Delete is Blocked / Under Review in this release.
      {
        key: "evaluations",
        moduleName: companyPermissionModules.evaluations,
        disabledFlags: ["can_delete"],
      },
      // Logistics issues (event Reports tab + /logistics_issues API):
      // View + Add (create/comment) + Edit (resolve). Delete stays off.
      {
        key: "reports",
        moduleName: companyPermissionModules.reports,
        disabledFlags: ["can_delete"],
      },
      // Survey Score is a read-only analytics view — only View applies.
      {
        key: "surveyScore",
        moduleName: companyPermissionModules.surveyScore,
        disabledFlags: ["can_add", "can_edit", "can_delete"],
      },
      // Heatmap is a visualization layer — only View applies.
      {
        key: "heatmap",
        moduleName: companyPermissionModules.heatmap,
        disabledFlags: ["can_add", "can_edit", "can_delete"],
      },
      {
        key: "rolesManagement",
        moduleName: companyPermissionModules.rolesManagement,
      },
    ],
  },
];

export const ALL_MODULES = PERMISSION_GROUPS.flatMap((group) => group.modules);

export const isLevelEnabled = (module, flag) =>
  !(module.disabledFlags || []).includes(flag);

export const enabledLevelsFor = (module) =>
  PERMISSION_LEVELS.filter((level) => isLevelEnabled(module, level.flag));

/** Empty permissions state keyed by module_name, every flag false. */
export const createEmptyPermissions = () =>
  ALL_MODULES.reduce((acc, module) => {
    acc[module.moduleName] = {
      can_view: false,
      can_add: false,
      can_edit: false,
      can_delete: false,
    };
    return acc;
  }, {});

/** Permissions payload for the create/update API body. Runs the dependency
 * cascade so the saved payload is self-consistent (the backend persists
 * verbatim what it's sent). Disabled cells are always sent as `false`. */
export const toPermissionsPayload = (permissions) => {
  const { effective } = applyPermissionDependencies(permissions);
  return ALL_MODULES.map((module) => ({
    module_name: module.moduleName,
    ...effective[module.moduleName],
    ...(module.disabledFlags || []).reduce(
      (acc, flag) => ({ ...acc, [flag]: false }),
      {},
    ),
  }));
};

/** Dirty-check compares effective (derived) maps, so a change that only
 * triggers a cascade — with no new explicit click — still marks the form dirty. */
export const arePermissionsEqual = (left, right) => {
  const { effective: effectiveLeft } = applyPermissionDependencies(left || {});
  const { effective: effectiveRight } = applyPermissionDependencies(right || {});
  return ALL_MODULES.every((module) => {
    const moduleName = module.moduleName;
    const a = effectiveLeft[moduleName];
    const b = effectiveRight[moduleName];
    return PERMISSION_LEVELS.every(
      (level) => Boolean(a[level.flag]) === Boolean(b[level.flag]),
    );
  });
};
